// Signing in, choosing a password, accepting an invitation and the access guards.
import type { ReactNode } from "react";

import { rich } from "../../helpers";

export default {
  layout: {
    headline: "Your agents. Your host.",
    headlineKeys: "Your keys.",
    facts: {
      isolation: "Each organisation gets isolated environments with their own network, VPN and Docker.",
      workers: "Agents run in disposable workers. Credentials stay with the broker on the host.",
      e2ee: "Conversations are end-to-end encrypted to your devices. The host cannot read them.",
    },
    footer: "Private host · no sign-up",
  },
  signIn: {
    title: "Sign in",
    lead: (host: ReactNode) => rich("to the agent cloud on ", host),
    email: "Email",
    emailPlaceholder: "you@example.com",
    password: "Password",
    passwordPlaceholder: "Your password",
    submit: "Sign in",
    failed: "Not signed in",
    tooManyAttempts: "Too many attempts. Wait a few minutes and try again.",
    wrongCredentials: "Email or password is incorrect.",
    noAccountTitle: "No account?",
    noAccount: (users: ReactNode) =>
      rich(" This is a private host; there is no sign-up. Ask its owner to add you from ", users, ". Forgotten your password? The owner can set a new one."),
    usersPage: "Users",
  },
  password: {
    change: "Change password",
    current: "Current password",
    new: "New password",
    confirm: "Confirm new password",
    update: "Update password",
    matches: "Passwords match",
    notChanged: "Password not changed",
    hintTooShort: (min: number) => `Use at least ${min} characters`,
    strong: "Strong",
    fair: "Fair; longer is stronger",
    checkedLocally: "checked in this browser, never logged",
    tooShort: (min: number) => `Use at least ${min} characters.`,
    differ: "The two passwords differ.",
  },
  choose: {
    title: "Choose a new password",
    lead: (email: ReactNode) => rich(email, " was created with a temporary password. Change it to continue; every other session of yours ends."),
    submit: "Set password and continue",
    signOutInstead: "Sign out instead",
  },
  invite: {
    title: "Choose your password",
    lead: "You were invited to the agent cloud on this host. The link works once.",
    form: "Accept invitation",
    password: "Password",
    repeat: "Repeat password",
    notAccepted: "Not accepted",
    submit: "Set password",
    invalid: "This invitation link is invalid, used or expired. Ask for a new one.",
    doneTitle: "You are in",
    doneLead: (email: ReactNode) => rich("Your password is set for ", email, "."),
    deviceCode: "Device code",
    linkPhone: "Link your phone",
    linkPhoneHelp: (expires: string) =>
      `Install the app on your phone, sign in with this email and password, and enter this one-time device code when it asks. It links your first device, which then approves your others. It is shown only now and works once, until ${expires}.`,
    oneTimeCode: "One-time device code",
    signIn: "Sign in",
  },
  guard: {
    checkingAccess: "Checking your access…",
    checkingSession: "Checking your session…",
    sessionError: "Could not check your session",
    notAvailable: "Not available",
    notAvailableToRole: "Not available to your role",
    superAdminOnly: "Only a Super Admin can open this page.",
    needsPermission: (permission: string) => `This page needs the ${permission} permission.`,
    askAdmin: "Ask an admin if you need it.",
    home: "Home",
  },
  notFound: {
    title: "Nothing here",
    body: (home: ReactNode) => rich("That page does not exist. ", home, "."),
    backHome: "Back to Home",
  },
};
